import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useMotionValue, useMotionTemplate, AnimatePresence } from 'framer-motion';
import { ArrowRight, Code, Smartphone, Palette, Globe, Layers, Zap, Menu, X, ChevronRight, Sparkles, Server, Cloud, Database } from 'lucide-react';
import "./index.css";
import logo from './logo.svg';

const BackgroundAnimation = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none -z-40 bg-[#030712]">
      {/* Grid pattern with mask */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
      
      {/* Ambient glowing orbs */}
      <motion.div 
        animate={{ scale: [1, 1.1, 1], opacity: [0.15, 0.3, 0.15], x: [0, 40, 0], y: [0, 30, 0] }} 
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-[-20%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-emerald-500/20 blur-[120px] mix-blend-screen"
      />
      <motion.div 
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1], x: [0, -50, 0], y: [0, 50, 0] }} 
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-[-20%] right-[-10%] w-[60vw] h-[60vw] rounded-full bg-teal-500/20 blur-[150px] mix-blend-screen"
      />
      <motion.div 
        animate={{ scale: [1, 1.3, 1], opacity: [0.1, 0.25, 0.1], x: [0, 60, 0], y: [0, -60, 0] }} 
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-[20%] right-[10%] w-[30vw] h-[30vw] rounded-full bg-primary/20 blur-[100px] mix-blend-screen"
      />
      
      {/* Floating glass morphic shapes - optimized for performance */}
      {Array.from({ length: 3 }).map((_, i) => (
        <motion.div
          key={i}
          className={`absolute backdrop-blur-sm ${i % 2 === 0 ? 'rounded-full' : 'rounded-[2rem] rotate-12'} border border-white/10 bg-white/5 shadow-2xl will-change-transform`}
          style={{
            width: Math.random() * 80 + 40,
            height: Math.random() * 80 + 40,
            left: `${Math.random() * 80 + 10}%`,
            top: `${Math.random() * 80 + 10}%`,
          }}
          animate={{
            y: [0, Math.random() * 60 - 30, 0],
            x: [0, Math.random() * 60 - 30, 0],
            rotate: [0, Math.random() * 180 + 90, 0],
          }}
          transition={{
            duration: Math.random() * 15 + 25,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}
    </div>
  );
};

const SpotlightCard = ({ children, className = "" }: { children: React.ReactNode, className?: string }) => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = ({ currentTarget, clientX, clientY }: React.MouseEvent) => {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  };

  return (
    <div
      onMouseMove={handleMouseMove}
      className={`group relative rounded-[2rem] border border-white/10 bg-white/[0.02] overflow-hidden ${className}`}
    >
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-[2rem] opacity-0 transition duration-300 group-hover:opacity-100 z-0"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              650px circle at ${mouseX}px ${mouseY}px,
              rgba(16, 185, 129, 0.15),
              transparent 80%
            )
          `,
        }}
      />
      <div className="relative z-10 h-full w-full">{children}</div>
    </div>
  );
};

const Marquee = () => {
  const technologies = [
    { name: "React", icon: <Code className="w-6 h-6 text-emerald-500/70" /> },
    { name: "Next.js", icon: <Layers className="w-6 h-6 text-teal-400/70" /> },
    { name: "TypeScript", icon: <Code className="w-6 h-6 text-blue-400/70" /> },
    { name: "Node.js", icon: <Server className="w-6 h-6 text-emerald-400/70" /> },
    { name: "Tailwind CSS", icon: <Palette className="w-6 h-6 text-cyan-400/70" /> },
    { name: "Framer Motion", icon: <Sparkles className="w-6 h-6 text-purple-400/70" /> },
    { name: "GraphQL", icon: <Globe className="w-6 h-6 text-pink-400/70" /> },
    { name: "AWS", icon: <Cloud className="w-6 h-6 text-orange-400/70" /> },
    { name: "PostgreSQL", icon: <Database className="w-6 h-6 text-blue-300/70" /> },
    { name: "React Native", icon: <Smartphone className="w-6 h-6 text-emerald-500/70" /> }
  ];
  
  return (
    <div className="w-full overflow-hidden py-10 border-y border-white/5 bg-black/20 relative flex">
      <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#030712] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#030712] to-transparent z-10 pointer-events-none" />
      
      <div className="flex animate-[marquee_40s_linear_infinite] w-max will-change-transform contain-paint">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="flex shrink-0 items-center gap-16 px-8">
            {technologies.map((tech, j) => (
              <div key={j} className="flex items-center gap-3">
                {tech.icon}
                <span className="text-xl md:text-2xl font-bold text-white/40 whitespace-nowrap">
                  {tech.name}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const App = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);

  useEffect(() => {
    // Force dark mode globally for premium feel
    document.documentElement.classList.add('dark');
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen text-slate-200 overflow-x-hidden font-sans selection:bg-emerald-500/30 selection:text-emerald-200">
      <BackgroundAnimation />
      
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${isScrolled ? 'bg-[#030712]/80 backdrop-blur-xl border-b border-white/10 shadow-2xl py-4' : 'bg-transparent py-6'}`}>
        <div className="container mx-auto px-6 lg:px-12 flex justify-between items-center">
          <div className="flex items-center gap-3 group cursor-pointer">
            <img src={logo} alt="Karsmiths Logo" className="w-10 h-10 group-hover:scale-105 transition-all duration-300" />
            <span className="font-bold text-2xl tracking-tight text-white">Karsmiths<span className="text-emerald-400">.</span></span>
          </div>

          <div className="hidden md:flex items-center gap-10 text-sm font-medium">
            {['Services', 'Our Work', 'About Us'].map((item) => (
              <a key={item} href={`#${item.toLowerCase().replace(' ', '-')}`} className="text-slate-300 hover:text-white transition-colors relative group">
                {item}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-emerald-400 transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
          </div>

          <div className="hidden md:flex">
            <a href="https://wa.me/+917008682347?text=Hi%20Karsmiths%20Solutions,%20I'd%20like%20to%20start%20a%20project!" target="_blank" rel="noopener noreferrer" 
               className="relative overflow-hidden bg-white/5 text-white border border-white/10 px-6 py-2.5 rounded-full font-medium text-sm transition-all duration-300 hover:bg-white/10 hover:border-emerald-500/50 hover:shadow-[0_0_20px_rgba(16,185,129,0.3)] flex items-center gap-2 group">
              <span className="relative z-10 flex items-center gap-2">Start Project <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"></div>
            </a>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden overflow-hidden bg-[#030712]/95 backdrop-blur-xl border-t border-white/10"
            >
              <div className="flex flex-col px-6 py-6 gap-4">
                {['Services', 'Our Work', 'About Us'].map((item) => (
                  <a 
                    key={item} 
                    href={`#${item.toLowerCase().replace(' ', '-')}`} 
                    className="text-slate-300 hover:text-white font-medium text-xl py-3 border-b border-white/5"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item}
                  </a>
                ))}
                <a 
                  href="https://wa.me/+917008682347?text=Hi%20Karsmiths%20Solutions,%20I'd%20like%20to%20start%20a%20project!" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="mt-6 flex items-center justify-center gap-2 bg-emerald-500 text-white py-4 rounded-xl font-bold text-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Start Project <ChevronRight className="w-5 h-5" />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 md:pt-56 md:pb-40 overflow-hidden">
        <div className="container mx-auto px-6 relative z-10 text-center flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-emerald-500/10 text-emerald-300 font-medium text-sm mb-10 border border-emerald-500/20 backdrop-blur-md"
          >
            <Sparkles className="w-4 h-4" />
            <span>Available for new projects</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            className="text-6xl md:text-8xl lg:text-9xl font-extrabold tracking-tight mb-8 max-w-6xl mx-auto leading-[1.1]"
          >
            We craft <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-emerald-500 animate-[gradient_8s_ease_infinite] bg-[length:200%_auto]">digital futures</span> that inspire.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-xl md:text-2xl text-slate-400 mb-12 max-w-3xl mx-auto font-light leading-relaxed"
          >
            A forward-thinking software agency specializing in building beautiful, performant, and hyper-scalable digital products.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full sm:w-auto"
          >
            <a href="https://wa.me/+917008682347?text=Hi%20Karsmiths%20Solutions,%20I'd%20like%20to%20discuss%20a%20project!" target="_blank" rel="noopener noreferrer" 
               className="w-full sm:w-auto px-10 py-5 bg-white text-black rounded-full font-bold text-lg hover:scale-105 hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-300 flex items-center justify-center gap-3 group">
              Let's Talk
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform" />
            </a>
            <button className="w-full sm:w-auto px-10 py-5 bg-white/5 text-white rounded-full font-bold text-lg hover:bg-white/10 transition-all duration-300 border border-white/10 backdrop-blur-md">
              View Portfolio
            </button>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* Stats Section */}
      <section className="py-24 border-y border-white/5 bg-black/40 backdrop-blur-lg relative z-10">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-12 divide-x divide-white/10">
            {[
              { label: "Projects Delivered", value: "150+" },
              { label: "Happy Clients", value: "90+" },
              { label: "Team Members", value: "25" },
              { label: "Years Experience", value: "5+" }
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: i * 0.1, type: "spring" }}
                className="text-center px-4"
              >
                <h3 className="text-5xl md:text-6xl font-extrabold text-white mb-3 tracking-tighter">{stat.value}</h3>
                <p className="text-emerald-400 font-medium tracking-wide uppercase text-sm">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-32 relative z-10">
        <div className="container mx-auto px-6">
          <div className="text-center mb-24">
            <h2 className="text-5xl md:text-6xl font-bold mb-8 text-white tracking-tight">Our Expertise</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-xl leading-relaxed">We bring your ideas to life with state-of-the-art technology and pixel-perfect design.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: <Globe className="w-8 h-8" />, title: "Web Development", desc: "High-performance, scalable web applications built with modern frameworks and serverless architectures." },
              { icon: <Smartphone className="w-8 h-8" />, title: "Mobile Apps", desc: "Cross-platform mobile experiences that users love, delivering native performance seamlessly." },
              { icon: <Palette className="w-8 h-8" />, title: "UI/UX Design", desc: "Intuitive, stunning interfaces designed for optimal user engagement and retention." },
              { icon: <Code className="w-8 h-8" />, title: "Custom Software", desc: "Tailored enterprise solutions to automate, scale, and secure your business operations." },
              { icon: <Layers className="w-8 h-8" />, title: "API Development", desc: "Robust, lightning-fast secure backend architectures and microservices." },
              { icon: <Zap className="w-8 h-8" />, title: "Optimization", desc: "Performance tuning and scaling your existing applications for better conversion." },
            ].map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
              >
                <SpotlightCard className="p-10 h-full flex flex-col">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center mb-8 border border-emerald-500/20 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white transition-all duration-500 shadow-[0_0_30px_rgba(16,185,129,0)] group-hover:shadow-[0_0_30px_rgba(16,185,129,0.4)]">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white">{service.title}</h3>
                  <p className="text-slate-400 leading-relaxed text-lg flex-grow">{service.desc}</p>
                </SpotlightCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden z-10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-emerald-900/40 to-[#030712] backdrop-blur-2xl border border-emerald-500/20 rounded-[3rem] p-12 md:p-24 text-center relative overflow-hidden shadow-2xl"
          >
            {/* CTA Background accents */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-500/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10 flex flex-col items-center">
              <h2 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight text-white">Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">elevate</span> your brand?</h2>
              <p className="text-xl md:text-2xl text-slate-400 mb-12 max-w-3xl mx-auto">Let's build something extraordinary together. Our team of experts is ready to transform your vision into reality.</p>
              <a href="https://wa.me/+917008682347?text=Hi%20Karsmiths%20Solutions,%20let's%20get%20in%20touch!" target="_blank" rel="noopener noreferrer" 
                 className="px-12 py-6 bg-emerald-500 text-white rounded-full font-bold text-xl hover:bg-emerald-400 hover:shadow-[0_0_40px_rgba(16,185,129,0.5)] transition-all duration-300 transform hover:-translate-y-1 flex items-center gap-4 w-fit">
                Get in touch today <ArrowRight className="w-6 h-6" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#030712] pt-20 pb-10 border-t border-white/10 relative z-10">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-16 mb-20">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-8">
                <img src={logo} alt="Karsmiths Logo" className="w-10 h-10" />
                <span className="font-bold text-3xl tracking-tight text-white">Karsmiths<span className="text-emerald-400">.</span></span>
              </div>
              <p className="text-slate-400 max-w-md text-lg leading-relaxed">
                Crafting digital experiences that empower businesses to thrive in the modern world. Innovation meets execution.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-6 text-white">Company</h4>
              <ul className="space-y-4">
                <li><a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">About Us</a></li>
                <li><a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">Careers</a></li>
                <li><a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">News</a></li>
                <li><a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-lg mb-6 text-white">Social</h4>
              <ul className="space-y-4">
                <li><a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">Twitter</a></li>
                <li><a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">LinkedIn</a></li>
                <li><a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">GitHub</a></li>
                <li><a href="#" className="text-slate-400 hover:text-emerald-400 transition-colors">Dribbble</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-slate-500">
              © {new Date().getFullYear()} Karsmiths Solutions. All rights reserved.
            </div>
            <div className="flex gap-8 text-sm text-slate-500">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Global styles for animations that are hard to do with just Tailwind classes inline */}
      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes marquee {
          0% { transform: translate3d(0%, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
      `}</style>
    </div>
  );
};

export default App;
